import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, MessageSquareText, Mail, Phone, Globe } from "lucide-react";
import { BRAND } from "../lib/brand";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-black text-white" data-testid="privacy-page">
      {/* Header */}
      <header
        className="border-b border-white/10"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-back">
            <div className="bg-black rounded-full p-0.5 ring-1 ring-white/10">
              <img src={BRAND.logo} alt="Rockstar" className="h-8 w-8 rounded-full" />
            </div>
            <div>
              <div className="font-display text-white text-sm leading-none tracking-wide">
                ROCKSTAR
              </div>
              <div className="font-mono text-[9px] tracking-[0.28em] text-[color:var(--primary)] leading-none mt-0.5">
                ATV RENTALS · FL
              </div>
            </div>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-mono text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={12} /> Back to Site
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-8 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-flex items-center gap-2 border border-[color:var(--primary)]/50 text-[color:var(--primary)] px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-mono">
            <ShieldCheck size={12} /> Legal
          </span>
          <h1 className="mt-5 font-display uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            Privacy Policy <span className="text-[color:var(--primary)]">&amp;</span>
            <br /> Messaging Terms.
          </h1>
          <p className="mt-4 text-[11px] uppercase tracking-widest font-mono text-white/50">
            Effective Date: August 13, 2026
          </p>
          <p className="mt-6 text-white/70 text-sm md:text-base leading-relaxed">
            Rockstar ATV Rentals LLC (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) respects your
            privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard
            your information when you visit our website at{" "}
            <a
              href="https://rockstaratvrentals.com/#top"
              className="text-[color:var(--primary)] link-under"
            >
              rockstaratvrentals.com
            </a>{" "}
            or interact with our customer support and text messaging services.
          </p>
        </motion.div>

        <Section id="collect" number="1" title="Information We Collect">
          <p>
            We collect information you provide directly to us when you make a booking inquiry,
            contact customer support, or opt-in to receive SMS communications. This information
            may include:
          </p>
          <List
            items={[
              "Full Name",
              "Phone Number",
              "Email Address",
              "Booking and rental inquiry details",
            ]}
          />
        </Section>

        <Section id="use" number="2" title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <List
            items={[
              "Provide all-terrain vehicle (ATV) rentals, guided tours, and outdoor adventure services.",
              "Respond to customer support inquiries and general questions.",
              "Send transactional updates, rental confirmations, and support messages.",
            ]}
          />
        </Section>

        <Section id="sharing" number="3" title="Data Sharing & Privacy Safeguards">
          <List
            items={[
              <>
                <strong className="text-white">Customer Data Protection:</strong> Customer data
                is not shared with third parties for promotional or marketing purposes.
              </>,
              <>
                <strong className="text-white">Mobile Opt-In &amp; Consent Data:</strong> Mobile
                opt-in and consent are never shared with anyone for any purpose. Any information
                sharing that may be mentioned elsewhere in this policy excludes mobile opt-in
                data.
              </>,
            ]}
          />
        </Section>

        <div id="messaging" className="mt-16 border-t border-white/10 pt-12 scroll-mt-24">
          <span className="inline-flex items-center gap-2 border border-white/25 text-white/80 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-mono">
            <MessageSquareText size={12} /> SMS
          </span>
          <h2 className="mt-4 font-display uppercase text-3xl md:text-4xl leading-[0.95]">
            Rockstar ATV Rentals LLC
            <br />
            <span className="text-[color:var(--primary)]">Messaging Terms &amp; Conditions.</span>
          </h2>
        </div>

        <NumberedItem n="1" title="Program Description">
          The messaging program consists of general conversational messaging to answer questions
          and provide support to customers regarding ATV rentals and bookings.
        </NumberedItem>

        <NumberedItem n="2" title="Cancellation (Opt-Out)">
          You can cancel the SMS service at any time. Just text <strong className="text-white">STOP</strong>{" "}
          to the phone number from which you received messages. After you send the SMS message
          STOP to us, we will send you an SMS message to confirm that you have been unsubscribed.
          After this, you will no longer receive SMS messages from us. If you want to join again,
          just sign up as you did the first time and we will start sending SMS messages to you
          again.
        </NumberedItem>

        <NumberedItem n="3" title="Customer Support">
          If you are experiencing issues with the messaging program, you can reply with the keyword{" "}
          <strong className="text-white">HELP</strong> for more assistance, or you can get help
          directly at{" "}
          <a
            href={`mailto:${BRAND.email}`}
            className="text-[color:var(--primary)] link-under break-all"
          >
            {BRAND.email}
          </a>
          .
        </NumberedItem>

        <NumberedItem n="4" title="Carrier Liability">
          Carriers are not liable for delayed or undelivered messages.
        </NumberedItem>

        <NumberedItem n="5" title="Message & Data Rates">
          As always, message and data rates may apply for any messages sent to you from us and to
          us from you. Message frequency will vary based on communication needs. If you have any
          questions about your text plan or data plan, it is best to contact your wireless
          provider.
        </NumberedItem>

        <NumberedItem n="6" title="Privacy">
          If you have any questions regarding privacy, please review this full privacy policy
          document.
        </NumberedItem>

        <Section id="contact" number="5" title="Contact Us">
          <p className="mb-4">
            If you have questions or concerns about this policy, please contact us:
          </p>
          <ul className="space-y-2.5">
            <li>
              <a
                href={`mailto:${BRAND.email}`}
                className="link-under inline-flex items-center gap-2 text-sm break-all"
              >
                <Mail size={13} className="text-[color:var(--primary)]" /> {BRAND.email}
              </a>
            </li>
            <li>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="link-under inline-flex items-center gap-2 text-sm"
              >
                <Phone size={13} className="text-[color:var(--primary)]" /> {BRAND.phone}
              </a>
            </li>
            <li>
              <a
                href="https://rockstaratvrentals.com/#top"
                className="link-under inline-flex items-center gap-2 text-sm break-all"
              >
                <Globe size={13} className="text-[color:var(--primary)]" />{" "}
                rockstaratvrentals.com
              </a>
            </li>
          </ul>
        </Section>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[10px] uppercase tracking-widest font-mono text-white/40">
          <span>© {new Date().getFullYear()} Rockstar ATV Rentals LLC</span>
          <Link to="/" className="hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ id, number, title, children }) {
  return (
    <section
      id={id}
      className="mt-12 md:mt-14 scroll-mt-24"
      data-testid={`privacy-section-${id}`}
    >
      <h2 className="font-display uppercase text-2xl md:text-3xl leading-[0.95] flex items-baseline gap-3">
        <span className="text-[color:var(--primary)] font-mono text-lg">{number}.</span> {title}
      </h2>
      <div className="mt-4 text-white/70 text-sm md:text-base leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-2 mt-3">
      {items.map((it, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-1.5 shrink-0 w-1.5 h-1.5 bg-[color:var(--primary)] rounded-full" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function NumberedItem({ n, title, children }) {
  return (
    <div className="mt-6 border-l-2 border-white/15 pl-5">
      <p className="text-[10px] uppercase tracking-widest font-mono text-[color:var(--primary)]">
        {n}. {title}
      </p>
      <p className="mt-1.5 text-white/70 text-sm md:text-base leading-relaxed">{children}</p>
    </div>
  );
}
